import { Client, type IFrame, type IMessage, type StompSubscription } from '@stomp/stompjs';
import SockJS from 'sockjs-client';

/**
 * Singleton STOMP/WebSocket client dla TMS.
 *
 * Auth: token JWT przesyłany w STOMP CONNECT frame jako `Authorization: Bearer <token>` —
 * backend `JwtChannelInterceptor` weryfikuje przy CONNECT i SUBSCRIBE (per topic).
 *
 * Reconnect: exponential backoff przez `reconnectDelay` (5s base) — STOMP.js auto-resubscribuje
 * topics po reconnection (deklaratywne, nie ręczne tracking).
 *
 * Lifecycle:
 *   - `connect(getToken)` — wywołuje na login (AuthContext)
 *   - `subscribe(topic, handler)` — wywołuje per-component (mountuje topic listener)
 *   - `disconnect()` — wywołuje na logout
 *
 * Token jest dynamiczny: `getToken()` pobiera aktualny access token (po refresh interceptor
 * zaktualizował) — nie cache'ujemy w client config bo wygasa co 15 min.
 */

type TokenProvider = () => string | null;

class WsClient {
  private client: Client | null = null;
  private getToken: TokenProvider = () => null;

  connect(getToken: TokenProvider): void {
    // No-op w testach — jsdom nie ma WebSocket constructora, SockJS rzuci unhandled error.
    // Production builds nie mają MODE === 'test'.
    if (import.meta.env.MODE === 'test') return;

    if (this.client?.active) {
      // Re-connect z nowym token providerem (np. nowy login)
      this.disconnect();
    }
    this.getToken = getToken;
    this.client = new Client({
      // SockJS factory — fallback dla browserów bez WebSocket lub corporate proxy.
      // Endpoint /ws → backend WebSocketConfig.registerStompEndpoints
      webSocketFactory: () => new SockJS('/ws'),
      reconnectDelay: 5_000,
      heartbeatIncoming: 10_000,
      heartbeatOutgoing: 10_000,
      // STOMP CONNECT frame headers — dynamiczne, recomputowane przy każdym (re)connect
      beforeConnect: () => {
        const token = this.getToken();
        if (token && this.client) {
          this.client.connectHeaders = { Authorization: `Bearer ${token}` };
        }
      },
      onStompError: (frame: IFrame) => {
        // Server-side error (auth failure, invalid subscribe) — log, ale nie crash UI.
        // Reconnect logic w STOMP.js automatycznie spróbuje ponownie.
        console.warn('[ws] STOMP error', frame.headers['message'], frame.body);
      },
      onWebSocketError: (event: Event) => {
        console.warn('[ws] connection error', event);
      },
    });
    this.client.activate();
  }

  /**
   * Subscribe na topic z handlerem. Zwraca unsubscribe function (do useEffect cleanup).
   * Bezpieczne wywołanie przed `connect()` lub przed activated state — STOMP.js queue'uje
   * subscription i wykona po połączeniu.
   */
  subscribe<T = unknown>(
    destination: string,
    handler: (payload: T, frame: IMessage) => void,
  ): () => void {
    if (!this.client) {
      // Client jeszcze nie zinicjalizowany — return no-op cleanup, caller dostaje tę
      // subscription gdy connect() zostanie wywołane (np. po login).
      return () => {};
    }
    let sub: StompSubscription | null = null;
    const tryInit = (): void => {
      if (!this.client?.connected) return;
      sub = this.client.subscribe(destination, (frame) => {
        try {
          const payload = JSON.parse(frame.body) as T;
          handler(payload, frame);
        } catch (e) {
          console.warn('[ws] failed to parse frame body', e);
        }
      });
    };
    tryInit();
    // Jeśli klient nie jest jeszcze connected, zaczekaj na onConnect i subskrybuj wtedy.
    if (!sub && this.client) {
      const previousOnConnect = this.client.onConnect;
      this.client.onConnect = (frame) => {
        previousOnConnect?.(frame);
        tryInit();
      };
    }
    return () => {
      sub?.unsubscribe();
      sub = null;
    };
  }

  disconnect(): void {
    if (this.client?.active) {
      void this.client.deactivate();
    }
    this.client = null;
  }

  isConnected(): boolean {
    return this.client?.connected ?? false;
  }
}

export const wsClient = new WsClient();
